import {
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ReferenceLine,
  TooltipContentProps,
} from 'recharts';
import {chartBox, xAxisTickStyle, yAxisTickStyle} from './styles';
import {Color} from '~/constants/style';
import {CustomDot} from './CustomDot';
import {Box} from '@mui/material';
import TooltipContent from './TooltipContent';
import {useState} from 'react';
import {isFutureMonth} from '~/utils/helper';
import {MonthlyRevenueApiItem} from '$api/finance/dashboard';

interface ExpandedMonthlyRevenueChartProps {
  chartData: {
    month: string;
    value: number;
    bookingRate: number;
    estimatedRevenue: number;
  }[];
  monthlyData: MonthlyRevenueApiItem[];
  yAxisTicks: number[];
}

type CustomTooltipProps = TooltipContentProps<number, string> & {
  monthlyData?: MonthlyRevenueApiItem[];
};

const CustomTooltip = (props: CustomTooltipProps) => {
  const {active, monthlyData, payload, label} = props;

  if (
    !active ||
    !payload?.length ||
    typeof label !== 'string' ||
    !monthlyData?.length
  )
    return null;

  const data = monthlyData.find(d => d?.month === label);
  if (!data) return null;
  const isFuture = isFutureMonth(label);

  return (
    <TooltipContent
      month={label}
      revenue={data.revenue || 0}
      estimatedRevenue={data.estimated_revenue || 0}
      bookingRate={data.booking_rate || 0}
      isFuture={isFuture}
    />
  );
};

const ExpandedMonthlyRevenueChart = ({
  chartData,
  monthlyData,
  yAxisTicks,
}: ExpandedMonthlyRevenueChartProps) => {
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(
    null
  );

  if (!chartData?.length || !monthlyData?.length || !yAxisTicks?.length) {
    return null;
  }

  const handleMouseMove = (state: {
    activeTooltipIndex?: number | string | null;
  }) => {
    if (state && typeof state.activeTooltipIndex === 'number') {
      setActiveTooltipIndex(state.activeTooltipIndex);
    }
  };
  const handleMouseLeave = () => setActiveTooltipIndex(null);

  const maxDomain = yAxisTicks[yAxisTicks.length - 1] || 50000;

  return (
    <Box sx={{...chartBox, mt: 2}}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{top: 20, right: 30, left: 0, bottom: 0}}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid
            stroke={Color.BorderGreyNormal}
            strokeDasharray="4 4"
            vertical={true}
            horizontal={false}
          />
          {yAxisTicks.map(y => (
            <ReferenceLine
              key={y}
              y={y}
              stroke={Color.BorderGreyNormal}
              ifOverflow="extendDomain"
            />
          ))}
          {activeTooltipIndex !== null && chartData[activeTooltipIndex] && (
            <ReferenceLine
              x={chartData[activeTooltipIndex].month}
              stroke={Color.Primary2Darkest}
              strokeWidth={3}
              ifOverflow="extendDomain"
            />
          )}
          <Area
            type="linear"
            dataKey="value"
            stroke={Color.Purple600}
            fill={Color.Purple600}
            fillOpacity={0.15}
            strokeWidth={3}
            dot={CustomDot}
            isAnimationActive={false}
          />
          <XAxis
            dataKey="month"
            tick={xAxisTickStyle}
            tickFormatter={monthStr => {
              if (!monthStr) return '';
              const [month] = monthStr.split(' ');
              return month.slice(0, 3);
            }}
            axisLine={false}
            tickLine={false}
          />

          <YAxis
            width={80}
            allowDataOverflow={true}
            tick={yAxisTickStyle}
            tickFormatter={v => `£${(v || 0).toLocaleString()}`}
            ticks={yAxisTicks}
            domain={[0, maxDomain]}
            scale="linear"
            tickCount={yAxisTicks.length}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            content={(props: TooltipContentProps<number, string>) => (
              <CustomTooltip
                {...props}
                active={props.active ?? false}
                monthlyData={monthlyData}
              />
            )}
            cursor={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ExpandedMonthlyRevenueChart;
